// قاب مشترک اپ‌ها: i18n ← Theme RTL ← TanStack Query ← AuthProvider (با حالت‌های آماده‌سازی، پایان نشست و خطا).
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { isApiError } from '@phoenix/api-client';
import { createPhoenixI18n, I18nextProvider, useTranslation } from '@phoenix/i18n';
import { EmptyState, ErrorState, LoadingState, PhoenixThemeProvider } from '@phoenix/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { AuthProvider } from './AuthProvider';
import type { AuthClient } from './authClient';

/** QueryClient پیش‌فرض: تکرار فقط برای خطای شبکه و ۵xx (نه 4xx)؛ بدون Refetch با فوکوس. */
export function createPhoenixQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) =>
          failureCount < 2 && (!isApiError(error) || error.status === 0 || error.status >= 500),
        refetchOnWindowFocus: false,
      },
    },
  });
}

function FullPage({ children }: { children: ReactNode }) {
  return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>{children}</Box>;
}

function SessionEnded({ onLogin }: { onLogin: () => void }) {
  const { t } = useTranslation('auth');
  return (
    <FullPage>
      <div data-screen-id="AUTH-SESSION-ENDED">
        <EmptyState
          titleKey="auth:session_ended.title"
          descriptionKey="auth:session_ended.description"
          action={
            <Button variant="contained" onClick={onLogin}>
              {t('auth:session_ended.login')}
            </Button>
          }
        />
      </div>
    </FullPage>
  );
}

export interface AppFrameProps {
  authClient: AuthClient;
  queryClient?: QueryClient;
  /** مسیرهای عمومی بدون ورود (AuthProvider.allowAnonymous — AP-IAM-02). */
  allowAnonymous?: (pathname: string) => boolean;
  children: ReactNode;
}

export function AppFrame({ authClient, queryClient, allowAnonymous, children }: AppFrameProps) {
  const [i18n] = useState(() => createPhoenixI18n());
  const [client] = useState(() => queryClient ?? createPhoenixQueryClient());

  return (
    <I18nextProvider i18n={i18n}>
      <PhoenixThemeProvider language={i18n.language}>
        <QueryClientProvider client={client}>
          <AuthProvider
            client={authClient}
            allowAnonymous={allowAnonymous}
            fallback={
              <FullPage>
                <LoadingState />
              </FullPage>
            }
            renderEnded={(login) => <SessionEnded onLogin={login} />}
            renderError={(error, retry) => (
              <FullPage>
                <ErrorState error={error} onRetry={retry} />
              </FullPage>
            )}
          >
            {children}
          </AuthProvider>
        </QueryClientProvider>
      </PhoenixThemeProvider>
    </I18nextProvider>
  );
}
